// src/hooks/useAnnouncements.js
//
// Live "What's New" hook (D-14 to D-19, 54-CONTEXT.md). Subscribes to the
// announcements collection as soon as a signed-in user is present -- not
// gated on sign-in itself, so a session that was already open when an
// announcement is published still sees it (D-15). Feeds both surfaces from
// one subscription: the pop-up (AnnouncementPopup, popupAnnouncements) and
// the Help menu's permanent "What's New" list (HelpMenuPanel, helpList).
//
// Scope of the counts (B-IN-07): report counts come from the workspace
// selected when the pop-up opens, while seen state is per user. A
// multi-workspace user therefore sees another workspace's counts only in the
// Help list after switching, which D-17 accepts.
//
// Gating (B-CR-01, B-WR-01): the pop-up stays empty until the user's seen ids
// have loaded (and forever if that read fails, D-15) and until every report an
// unseen announcement references has settled, so the D-17/D-31 counts are
// present when the dialog first renders.
//
// Errors (the listener, fetchSeenAnnouncementIds, fetchMigrationReport) are
// console.error'd and never thrown -- a load failure here must never block
// the app shell (T-54-10-02).

import React from 'react';
import * as announcementService from '../services/announcementService';
import { selectPopupAnnouncements, selectHelpListAnnouncements, selectHelpPanelAnnouncements } from '../utils/announcements';

// Default (production) implementations of every announcementService call
// this hook makes. Overridable ONLY via the __serviceDeps test seam below --
// no production caller ever passes it. This mirrors
// src/hooks/useTransactionHandlers.js's DEFAULT_SYNC_DEPS: this repo's
// esbuild-bundled tests compile every module's named exports into
// non-configurable getters, so node:test's t.mock.method cannot redefine
// announcementService's functions directly -- an explicit, narrow
// dependency-injection object is the seam that survives that bundling.
const DEFAULT_SERVICE_DEPS = {
    subscribeToAnnouncements: (...args) => announcementService.subscribeToAnnouncements(...args),
    fetchSeenAnnouncementIds: (...args) => announcementService.fetchSeenAnnouncementIds(...args),
    markAnnouncementsSeen: (...args) => announcementService.markAnnouncementsSeen(...args),
    fetchMigrationReport: (...args) => announcementService.fetchMigrationReport(...args),
};

/**
 * Firebase auth user.metadata.creationTime ("Wed, 01 Jan 2020 00:00:00 GMT")
 * -> 'YYYY-MM-DD', so it compares lexicographically against an announcement's
 * own 'YYYY-MM-DD' publishedAt (see 54-04-SUMMARY.md's key-decisions).
 * Returns null for an absent or unparseable value.
 *
 * @param {string|undefined} creationTime
 * @returns {string|null}
 */
function toDateOnly(creationTime) {
    if (!creationTime) return null;
    const parsed = new Date(creationTime);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().slice(0, 10);
}

/**
 * @param {object} params
 * @param {{uid: string, metadata?: {creationTime?: string}}|null} params.user
 * @param {string|null} [params.workspaceId]
 * @param {object} [params.__serviceDeps] - test-only override seam, see DEFAULT_SERVICE_DEPS
 * @returns {{helpList: Array, helpPanel: {items: Array, hasMore: boolean}, popupAnnouncements: Array, hasUnseen: boolean, error: Error|null, loading: boolean, ready: boolean, markShownSeen: (ids: Array<string>) => void}}
 */
export function useAnnouncements({ user, workspaceId, __serviceDeps } = {}) {
    const deps = { ...DEFAULT_SERVICE_DEPS, ...__serviceDeps };

    const [announcements, setAnnouncements] = React.useState([]);
    const [seenIds, setSeenIds] = React.useState([]);
    const [seenLoaded, setSeenLoaded] = React.useState(false);
    const [reportsById, setReportsById] = React.useState({});
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(Boolean(user));

    const uid = user?.uid || null;

    // Cached ref, not state: reportId -> report|null, fetched once per
    // workspace (D-17). Reset whenever the workspace changes so a report
    // fetched for one workspace never leaks into another's detail line.
    const reportCacheRef = React.useRef(new Map());
    React.useEffect(() => {
        reportCacheRef.current = new Map();
        setReportsById({});
    }, [workspaceId]);

    // Live listener (D-15) + the caller's own seen-state, keyed on uid alone
    // so signing in/out (not every render) opens/closes the subscription.
    React.useEffect(() => {
        if (!uid) {
            setAnnouncements([]);
            setSeenIds([]);
            setSeenLoaded(false);
            setError(null);
            setLoading(false);
            return undefined;
        }

        // A uid switch must not inherit the previous user's seen state.
        setSeenIds([]);
        setSeenLoaded(false);
        setLoading(true);
        setError(null);
        let cancelled = false;

        deps.fetchSeenAnnouncementIds(uid)
            .then((ids) => {
                if (cancelled) return;
                // Union, not replace: ids dismissed before the read resolved
                // must not reappear.
                setSeenIds((prev) => Array.from(new Set([...prev, ...(ids || [])])));
                setSeenLoaded(true);
            })
            .catch((err) => console.error('useAnnouncements: fetchSeenAnnouncementIds failed', err));

        const unsubscribe = deps.subscribeToAnnouncements(
            (next) => {
                if (cancelled) return;
                setAnnouncements(next);
                setLoading(false);
            },
            (err) => {
                if (cancelled) return;
                console.error('useAnnouncements: listener error', err);
                setError(err);
                setAnnouncements([]);
                setLoading(false);
            },
        );

        return () => {
            cancelled = true;
            if (typeof unsubscribe === 'function') unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    // Fetch the report for each distinct non-empty detailReportId the live
    // announcement list currently references, once per workspace --
    // reportCacheRef (not a dependency) is what makes this "once", not a
    // guard on this effect's own deps.
    React.useEffect(() => {
        if (!workspaceId) return undefined;
        const reportIds = Array.from(new Set(
            announcements.map((a) => a.detailReportId).filter(Boolean),
        ));
        const missing = reportIds.filter((id) => !reportCacheRef.current.has(id));
        if (!missing.length) return undefined;

        let cancelled = false;
        Promise.all(missing.map(async (reportId) => [reportId, await deps.fetchMigrationReport(workspaceId, reportId)]))
            .then((entries) => {
                if (cancelled) return;
                for (const [reportId, report] of entries) {
                    reportCacheRef.current.set(reportId, report);
                }
                setReportsById((prev) => {
                    const next = { ...prev };
                    for (const [reportId, report] of entries) next[reportId] = report;
                    return next;
                });
            })
            .catch((err) => console.error('useAnnouncements: fetchMigrationReport failed', err));

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [announcements, workspaceId]);

    const userCreatedAt = toDateOnly(user?.metadata?.creationTime);
    const parsedCreatedAtMs = Date.parse(user?.metadata?.creationTime || '');
    const userCreatedAtMs = Number.isNaN(parsedCreatedAtMs) ? null : parsedCreatedAtMs;

    // Every report an unseen announcement references has settled (a null
    // report counts as settled). Announcements with no detailReportId never
    // hold the pop-up. Without a workspace the counts cannot be known.
    const reportsReady = React.useMemo(() => {
        if (!workspaceId) return false;
        const seen = new Set(seenIds);
        return announcements
            .filter((a) => !seen.has(a.id) && a.detailReportId)
            .every((a) => Object.prototype.hasOwnProperty.call(reportsById, a.detailReportId));
    }, [announcements, seenIds, reportsById, workspaceId]);

    const ready = seenLoaded && reportsReady;

    const popupAnnouncements = React.useMemo(
        () => (ready
            ? selectPopupAnnouncements({
                announcements, seenIds, reportsById, userCreatedAt, userCreatedAtMs,
            })
            : []),
        [ready, announcements, seenIds, reportsById, userCreatedAt, userCreatedAtMs],
    );
    const helpList = React.useMemo(
        () => selectHelpListAnnouncements({ announcements, reportsById }),
        [announcements, reportsById],
    );

    // The Help panel's newest-few slice (D-26); helpList stays the full
    // history for the /whats-new page (D-27).
    const helpPanel = React.useMemo(
        () => selectHelpPanelAnnouncements({ announcements, reportsById }),
        [announcements, reportsById],
    );

    // Adds ids to local seen state immediately (so the pop-up closes without
    // waiting on the network), then persists the write. A rejection is
    // caught and logged -- never surfaced to the caller.
    const markShownSeen = React.useCallback((ids) => {
        if (!Array.isArray(ids) || !ids.length) return;
        setSeenIds((prev) => Array.from(new Set([...prev, ...ids])));
        if (!uid) return;
        deps.markAnnouncementsSeen(uid, ids).catch((err) => {
            console.error('useAnnouncements: markAnnouncementsSeen failed', err);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    return {
        helpList,
        helpPanel,
        popupAnnouncements,
        hasUnseen: popupAnnouncements.length > 0,
        error,
        loading,
        ready,
        markShownSeen,
    };
}

export default useAnnouncements;
